// components/PricingSection.jsx
import React, { useState } from "react";
import CheckIcon   from "@mui/icons-material/Check";
import CloseIcon   from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Reveal }  from "../lib/hooks";
import { PLANS }   from "../lib/data";

const ACCENT_CLASSES = {
  pink:  "bg-pink",
  teal:  "bg-teal",
  gold:  "bg-gold",
  navy:  "bg-navy",
};

const ACCENT_TEXT = {
  pink:  "text-pink",
  teal:  "text-teal",
  gold:  "text-gold",
  navy:  "text-navy dark:text-[#e6edf3]",
};

export default function PricingSection() {
  const [billing, setBilling] = useState("yearly");

  return (
    <section
      id="pricing"
      className="bg-[#f4f6fb] dark:bg-[#21262d] relative overflow-hidden"
      style={{background : "rgb(var(--canvas))"}}
    >

      <div className="max-w-full mx-auto px-14 py-16 text-center" id="card-container">
        <Reveal>
          <div className="section-label justify-center">Pricing</div>
          <h2 className="section-title">A plan for every creator</h2>
          <p className="pricing-sub-header mx-auto my-6">
            Start free. Upgrade when ready.
          </p>
        </Reveal>

        {/* Billing toggle */}
        <div className="flex justify-center mb-12">
          <div className="
            pricing-switcher pl-1
          ">
            {[
              { id: "monthly", label: "Monthly" },
              {
                id: "yearly",
                label: (
                  <span className="flex items-center gap-1.5">
                    Annual
                    <span className="ps-save">
                      Save 25%
                    </span>
                  </span>
                ),
              },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setBilling(id)}
                className={`
                  px-6 py-2 mt-1 rounded-full text-md font-semibold font-dm
                  cursor-pointer border-none transition-all duration-250
                  ${billing === id
                    ? "bg-pink-500 text-white shadow-[0_2px_12px_rgba(233,30,140,0.35)]"
                    : "bg-transparent text-[#5a6888] dark:text-[#8b949e]"
                  }
                `}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid lg:grid-cols-4 gap-4 sm:grid-cols-1">
          {PLANS.map((plan, i) => {
            const price = billing === "monthly" ? plan.monthly : plan.yearly;
            const saveText = billing === "monthly" ? plan.saveMo : plan.saveYr;

            return (
              <Reveal key={plan.id} delay={i * 70}>
                <div className={`
                    relative flex flex-col h-full rounded-2xl p-7
                    border transition-all duration-300
                    plan-card
                    ${plan.featured
                      ? "bg-white dark:bg-[#161b22] border-pink-400 shadow-[0_0_60px_rgba(233,30,140,0.15),0_0_0_1.5px_rgba(233,30,140,0.3)]"
                      : "bg-white dark:bg-[#161b22] border-[rgba(27,58,107,0.1)] dark:border-[rgba(255,255,255,0.06)]"
                    }
                    hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(27,58,107,0.1)]
                  `}>
                  {/* Badge */}
                  {plan.badge && (
                    <div className="
                      absolute -top-3 right-1/3 -translate-x-1/2
                      bg-pink-500 text-white text-sm font-bold tracking-[1.5px] uppercase
                      px-3 py-1 rounded-full whitespace-nowrap
                      shadow-[0_0_20px_rgba(233,30,140,0.5)]
                    ">
                      {plan.badge}
                    </div>
                  )}

                  {/* Plan name */}
                  {/* <div className={`font-syne text-2xl font-bold mb-1 ${plan.featured ? "navy-text" : "navy-text dark:text-[#e6edf3]"}`}>
                    {plan.name}
                  </div> */}
                  {/* <div className={`text-sm mb-6 leading-snug ${plan.featured ? "text-[#5a6888]" : "text-[#5a6888] dark:text-[#e6edf3]"}`}>
                    {plan.tag}
                  </div> */}
                  <div className={`plan-name mb-4`}>
                    {plan.name}
                  </div>
                  <div className={`plan-tag mb-6`}>
                    {plan.tag}
                  </div>

                  {/* Price */}
                  {price !== null ? (
                    <div className={`font-syne text-[38px] font-extrabold leading-none ${plan.featured ? "text-pink" : "text-navy dark:text-[#e6edf3]"}`}>
                      <span className="cur align-super">₹</span>
                      <span className="tabular-nums amt">{price.toLocaleString("en-IN")}</span>
                      {plan.id !== "free" && price !== null && (
                        <span className="per">/mo</span>
                      )}
                    </div>
                  ) : (
                    <div className={`font-syne text-2xl font-extrabold ${plan.featured ? "text-white" : "navy-text dark:text-[#e6edf3]"}`}>
                      Custom
                    </div>
                  )}

                  {/* Save text */}
                  <div className={`plan-helper-text my-2 ${ACCENT_TEXT[plan.accent]}`}>
                    {saveText}
                  </div>

                  {/* Divider */}
                  <div className="border-t my-1"></div>

                  {/* Features */}
                  <div className="flex-1 flex flex-col gap-3 mb-8">
                    {plan.features.map(f => (
                      <div key={f} className="flex items-start gap-2 plan-pf">
                        <CheckIcon sx={{ fontSize: 14, color: "#0a9e78", marginTop: "2px", flexShrink: 0 }} />
                        <span className={plan.featured ? "text-[#5a6888]" : "text-[#5a6888] dark:text-[#8b949e]"}>{f}</span>
                      </div>
                    ))}
                    {plan.disabled.map(f => (
                      <div key={f} className="flex items-start gap-2 plan-pf-no">
                        <CloseIcon sx={{ fontSize: 14, color: "red", marginTop: "2px", flexShrink: 0 }} />
                        <span className={plan.disabled ? "text-[#5a6888]" : "text-[#5a6888] dark:text-[#8b949e]"}>{f}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <a
                    href={plan.href}
                    className={`
                      block text-center py-3 rounded-xl font-semibold justify-center mx-2
                      no-underline transition-all duration-200 font-dm
                      ${plan.featured
                        ? "pink-btn shadow-[0_0_20px_rgba(233,30,140,0.3)] hover:bg-pink-dark"
                        : "ghost-btn hover:border-pink-200 hover:bg-pink-400"
                      }
                    `}
                  >
                    {plan.cta}
                  </a>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="pricing-note mt-5">
          Licences valid forever
        </p>
      </div>
    </section>
  );
}
